import { useRef, useState } from "react";
import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";

import { useAanraakscherm } from "../useSmalScherm";

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  /** Horizontal alignment for this column's header and cells. Defaults to "left".
   *  Use "right" for numeric columns so the digits line up on the decimal edge. */
  align?: "left" | "right" | "center";
  /** Sort key this column maps to, e.g. "username". Set it (together with the table's `sort` /
   *  `onSortChange`) to make the header a sort toggle. The key is what the caller sends to its
   *  server — the table never sorts rows itself. Omit for a non-sortable column (the default). */
  sortKey?: string;
  /** De vaste sleutel van deze kolom in een bewaarde indeling. Zonder sleutel geldt de kop als
   *  sleutel; hernoem je die kop later, dan valt de bewaarde indeling voor die kolom terug op de
   *  standaard -- hinderlijker is een sleutel die stilletjes de verkeerde kolom aanwijst. */
  key?: string;
  /** Deze kolom blijft staan waar hij staat: niet te verslepen en niet van breedte te veranderen.
   *  Voor een kolom die bij de rij hoort in plaats van bij de gegevens, zoals een knoppenkolom. */
  vast?: boolean;
}

/**
 * Hoe iemand zijn kolommen wil zien.
 *
 * <p>Niet iedereen leest een lijst hetzelfde: de een zoekt op datum, de ander op nummer. Daarom kan
 * elke kolom verschoven worden en kan hij breder of smaller. Wat hier staat is wat die persoon heeft
 * ingesteld; kolommen die er niet in voorkomen houden hun plek in de code en hun eigen breedte.
 */
export interface TabelIndeling {
  /** Kolomsleutels in de volgorde waarin ze moeten staan. Onbekende sleutels worden genegeerd. */
  volgorde?: string[];
  /** Breedte per kolomsleutel in pixels. */
  breedtes?: Record<string, number>;
}

/** De sleutel waaronder een kolom in een indeling staat. */
export function kolomSleutel<T>(kolom: Column<T>): string {
  return kolom.key ?? kolom.header;
}

/** Smaller dan dit wordt een kolom niet: dan is de kop niet meer te lezen en te pakken. */
export const MINIMALE_KOLOMBREEDTE = 60;

/**
 * De kolommen in de volgorde van de indeling.
 *
 * <p>Alles wat in de indeling staat komt eerst, in die volgorde; wat er niet in staat volgt erachter
 * in de volgorde van de code. Zo overleeft een bewaarde indeling een kolom die later is bijgekomen --
 * die verschijnt dan achteraan in plaats van dat de hele indeling wordt weggegooid.
 */
export function volgensIndeling<T>(kolommen: Column<T>[], indeling?: TabelIndeling): Column<T>[] {
  const volgorde = indeling?.volgorde;
  if (!volgorde || volgorde.length === 0) {
    return kolommen;
  }
  const over = [...kolommen];
  const uit: Column<T>[] = [];
  for (const sleutel of volgorde) {
    const i = over.findIndex((k) => kolomSleutel(k) === sleutel);
    if (i >= 0) {
      uit.push(over[i]);
      over.splice(i, 1);
    }
  }
  return [...uit, ...over];
}

/**
 * Een kolom verplaatsen naar de plek van een andere.
 *
 * <p>Geeft de nieuwe volgorde van alle sleutels terug -- ook van kolommen die nog niet in de indeling
 * stonden, want anders zou de volgende verschuiving ze alsnog achteraan gooien.
 */
export function naVerplaatsen<T>(kolommen: Column<T>[], van: string, naar: string): string[] {
  const sleutels = kolommen.map(kolomSleutel);
  const vanIndex = sleutels.indexOf(van);
  const naarIndex = sleutels.indexOf(naar);
  if (vanIndex < 0 || naarIndex < 0 || vanIndex === naarIndex) {
    return sleutels;
  }
  const uit = [...sleutels];
  uit.splice(vanIndex, 1);
  uit.splice(naarIndex, 0, van);
  return uit;
}

/** The table's current sort: which column key, and in which direction. */
export interface SortState {
  key: string;
  dir: "asc" | "desc";
}

function alignClass(align: Column<unknown>["align"]): string | undefined {
  if (align === "right") return "fui-table__col--right";
  if (align === "center") return "fui-table__col--center";
  return undefined;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  empty?: ReactNode;
  /** Called when a row is clicked. Makes rows interactive (pointer cursor) and is the hook for
   *  single-select master lists. Omit for a static, non-interactive table (the default). */
  onRowClick?: (row: T) => void;
  /** Called when a row is double-clicked — the hook for "open the editor on double-click" lists
   *  (e.g. the Jmix grids open the detail dialog on row double-click). Additive: when omitted the
   *  table emits no dblclick handler, keeping the default render byte-identical. */
  onRowDoubleClick?: (row: T) => void;
  /** The key (per `getRowKey`) of the currently selected row. The matching row gets `aria-selected`
   *  and the `fui-table__row--selected` highlight. When undefined, no row is marked selected and no
   *  `aria-selected` attribute is emitted — keeping the default render byte-identical. */
  selectedRowKey?: string;
  /** Extra class(es) for a row, e.g. per-row state styling. Merged after the built-in row classes. */
  rowClassName?: (row: T) => string | undefined;
  /** The active sort, for the matching column's `aria-sort` and indicator. Sorting is the caller's
   *  job (typically a server round-trip) — the table only reports the intent. */
  sort?: SortState;
  /** Called with the clicked column's `sortKey` and the direction it should become: the same column
   *  toggles asc↔desc, a different column starts at "asc". Together with a column `sortKey` this
   *  turns that header into a button; without it headers stay plain text (the default). */
  onSortChange?: (next: SortState) => void;
  /** De kolomindeling van deze gebruiker: volgorde en breedtes. Zonder indeling staat de tabel zoals
   *  de code hem opschrijft. */
  indeling?: TabelIndeling;
  /** Aanroepen zodra de gebruiker een kolom verschuift of van breedte verandert. Pas wanneer dit
   *  gezet is worden de koppen versleepbaar en krijgen ze een greep om breder te maken -- een tabel
   *  waarvan niemand de indeling bewaart hoort er niet anders uit te zien dan hij deed. */
  onIndelingChange?: (next: TabelIndeling) => void;
}

/** A token-styled list table — the generic primitive every list screen composes.
 *  Rows are static by default; pass `onRowClick` / `selectedRowKey` to make them a single-select list. */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  empty,
  onRowClick,
  onRowDoubleClick,
  selectedRowKey,
  rowClassName,
  sort,
  onSortChange,
  indeling,
  onIndelingChange,
}: DataTableProps<T>) {
  // Dubbelklikken bestaat niet op een aanraakscherm: een dubbeltik is daar een zoomgebaar, en een
  // lijst die zijn detail alleen op dblclick opent is met een vinger dus alleen te lezen. Dat geldt
  // net zo goed op een tablet als op een telefoon, dus hangt dit aan het soort aanwijzer en niet aan
  // de schermbreedte. Eén tik opent hetzelfde scherm — maar alleen als de aanroeper zelf niets aan
  // een enkele klik had gehangen, want dan zou die betekenis eroverheen gaan.
  const aanraak = useAanraakscherm();
  const opRijKlik = onRowClick ?? (aanraak ? onRowDoubleClick : undefined);

  // Verschuiven en verbreden kan alleen als iemand de uitkomst bewaart; anders zou je iets kunnen
  // instellen dat bij de eerstvolgende verversing weer weg is.
  const indeelbaar = onIndelingChange !== undefined;
  const getoondeKolommen = volgensIndeling(columns, indeling);
  const breedtes = indeling?.breedtes ?? {};
  const [sleept, setSleept] = useState<string | null>(null);
  const [doel, setDoel] = useState<string | null>(null);
  // Tijdens het slepen van de greep telt alleen waar de muis begon en hoe breed de kolom toen was.
  const grijpen = useRef<{ sleutel: string; startX: number; startBreedte: number } | null>(null);

  const bewaarBreedte = (sleutel: string, breedte: number) =>
    onIndelingChange?.({
      volgorde: indeling?.volgorde ?? columns.map(kolomSleutel),
      breedtes: { ...breedtes, [sleutel]: Math.max(MINIMALE_KOLOMBREEDTE, Math.round(breedte)) },
    });

  const verplaats = (van: string, naar: string) =>
    onIndelingChange?.({ volgorde: naVerplaatsen(getoondeKolommen, van, naar), breedtes });

  const beginGreep = (e: ReactPointerEvent<HTMLSpanElement>, sleutel: string) => {
    const kop = (e.currentTarget.closest("th") as HTMLElement | null);
    grijpen.current = {
      sleutel,
      startX: e.clientX,
      startBreedte: breedtes[sleutel] ?? kop?.getBoundingClientRect().width ?? MINIMALE_KOLOMBREEDTE,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const tijdensGreep = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const bezig = grijpen.current;
    if (!bezig) return;
    bewaarBreedte(bezig.sleutel, bezig.startBreedte + (e.clientX - bezig.startX));
  };
  const eindeGreep = () => {
    grijpen.current = null;
  };

  if (rows.length === 0) {
    return <p className="fui-muted">{empty ?? "Geen resultaten."}</p>;
  }
  // The wrapper confines a too-wide table's horizontal scroll to the table itself, so the app frame
  // (the working area) never grows a horizontal scrollbar. When the table fits, nothing scrolls.
  return (
    <div className="fui-table-wrap">
      <table className={indeelbaar ? "fui-table fui-table--indeelbaar" : "fui-table"}>
        {/* De breedtes staan in een colgroup en niet op elke cel: zo geldt één opgave voor de hele
            kolom, ook voor rijen die later binnenkomen. */}
        {Object.keys(breedtes).length > 0 && (
          <colgroup>
            {getoondeKolommen.map((c, i) => {
              const breedte = breedtes[kolomSleutel(c)];
              return <col key={i} style={breedte ? { width: `${breedte}px` } : undefined} />;
            })}
          </colgroup>
        )}
        <thead>
          <tr>
            {getoondeKolommen.map((c, i) => {
              // A header is only a sort toggle when the column names a key AND the caller can act on
              // it; otherwise it stays plain text and the th emits no aria-sort (render unchanged).
              const sortable = c.sortKey !== undefined && onSortChange !== undefined;
              // undefined = this column is not the active sort (so also not sortable at all).
              const activeDir = sortable && sort !== undefined && sort.key === c.sortKey ? sort.dir : undefined;
              const sleutel = kolomSleutel(c);
              // Een vaste kolom hoort bij de rij en niet bij de gegevens (een knoppenkolom); die
              // verslepen zou alleen maar een gat in de rij opleveren.
              const teVerplaatsen = indeelbaar && !c.vast;
              const klassen = [
                alignClass(c.align),
                teVerplaatsen ? "fui-table__col--indeelbaar" : undefined,
                doel === sleutel && sleept !== null && sleept !== sleutel ? "fui-table__col--doel" : undefined,
              ].filter(Boolean).join(" ") || undefined;
              return (
                <th
                  // De plek in de rij blijft de React-sleutel. De kolomsleutel is daar ongeschikt
                  // voor: twee kolommen zonder kop ("" als sleutel) komen in dit scherm echt voor,
                  // en twee gelijke React-sleutels naast elkaar is een bug die zich pas laat zien
                  // als er een rij verandert.
                  key={i}
                  className={klassen}
                  // Niet `false` maar niets: `draggable` is een opsommend attribuut, dus
                  // `false` zou in elke tabel een attribuut toevoegen dat er nooit stond.
                  draggable={teVerplaatsen ? true : undefined}
                  onDragStart={teVerplaatsen ? (e) => {
                    setSleept(sleutel);
                    e.dataTransfer.effectAllowed = "move";
                    // Firefox begint pas te slepen als er iets in het pakketje zit.
                    e.dataTransfer.setData("text/plain", sleutel);
                  } : undefined}
                  onDragOver={teVerplaatsen ? (e) => {
                    e.preventDefault();
                    setDoel(sleutel);
                  } : undefined}
                  onDrop={teVerplaatsen ? (e) => {
                    e.preventDefault();
                    const van = sleept ?? e.dataTransfer.getData("text/plain");
                    if (van && van !== sleutel) verplaats(van, sleutel);
                    setSleept(null);
                    setDoel(null);
                  } : undefined}
                  onDragEnd={teVerplaatsen ? () => { setSleept(null); setDoel(null); } : undefined}
                  aria-sort={
                    !sortable ? undefined : activeDir === undefined ? "none"
                      : activeDir === "desc" ? "descending" : "ascending"
                  }
                >
                  {sortable && c.sortKey !== undefined && onSortChange !== undefined ? (
                    <button
                      type="button"
                      className="fui-table__sort"
                      onClick={() =>
                        onSortChange({ key: c.sortKey!, dir: activeDir === "asc" ? "desc" : "asc" })
                      }
                    >
                      {c.header}
                      <span aria-hidden="true" className="fui-table__sort-caret">
                        {activeDir === undefined ? "↕" : activeDir === "desc" ? "▾" : "▴"}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                  {teVerplaatsen && (
                    <>
                      {/* Slepen werkt niet met een toetsenbord, dus verschuift deze knop de kolom ook
                          met de pijltjes -- anders is de indeling alleen met een muis te bedienen. */}
                      <button
                        type="button"
                        className="fui-table__verplaats"
                        aria-label={`Kolom ${c.header} verplaatsen`}
                        onKeyDown={(e) => {
                          const buur = e.key === "ArrowLeft" ? getoondeKolommen[i - 1]
                            : e.key === "ArrowRight" ? getoondeKolommen[i + 1] : null;
                          if (!buur || buur.vast) return;
                          e.preventDefault();
                          verplaats(sleutel, kolomSleutel(buur));
                        }}
                      >
                        <span aria-hidden="true">⠿</span>
                      </button>
                      <span
                        className="fui-table__greep"
                        role="separator"
                        aria-label={`Breedte van kolom ${c.header}`}
                        onPointerDown={(e) => beginGreep(e, sleutel)}
                        onPointerMove={tijdensGreep}
                        onPointerUp={eindeGreep}
                        onPointerCancel={eindeGreep}
                      />
                    </>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = getRowKey(row);
            const selected = selectedRowKey !== undefined && key === selectedRowKey;
            // Empty string → `className={undefined}` so a static table emits no class attribute.
            const cls =
              [
                opRijKlik || onRowDoubleClick ? "fui-table__row--clickable" : undefined,
                selected ? "fui-table__row--selected" : undefined,
                rowClassName?.(row),
              ]
                .filter(Boolean)
                .join(" ") || undefined;
            return (
              <tr
                key={key}
                className={cls}
                aria-selected={selectedRowKey !== undefined ? selected : undefined}
                onClick={opRijKlik ? () => opRijKlik(row) : undefined}
                onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
              >
                {getoondeKolommen.map((c, i) => (
                  // data-label draagt de kolomkop mee de cel in. Op een breed scherm doet dat
                  // niets — het is een attribuut, geen inhoud — maar op een telefoon, waar een
                  // tabel van acht kolommen niet past, kan de opmaak de rij daarmee als kaartje
                  // tonen met bij elke waarde het kopje waar hij bij hoort. Zonder dit label is
                  // een losse cel betekenisloos: "1897" zonder "Chipnummer" ervoor.
                  <td key={i} className={alignClass(c.align)} data-label={c.header}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
